import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, ShoppingCart } from "lucide-react";
import { rupiah, angka } from "../../lib/format";
import { coverThumb } from "../../lib/media";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { toastGalatKeranjang } from "../../lib/batasPcs";
import { pesanGalatPembeli } from "../../lib/galatPembeli";

function Card({ p }) {
  const { user } = useAuth();
  const { addItem, summary } = useCart();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const img = coverThumb(p);
  const kosong = p.ketersediaan === "KOSONG";
  const href = `/produk/${p.slug || p.id}`;
  const diKeranjang = (summary?.categories || []).some((c) =>
    (c.items || []).some((it) => it.produk_id === p.id));

  const tambah = async () => {
    if (!user) { nav("/login", { state: { from: href } }); return; }
    const qty = p.minimum_pcs || 500;
    setBusy(true);
    try {
      await addItem(p.id, qty);
      toast.success(`${angka(qty)} pcs ${p.nama} ditambahkan ke keranjang`);
    } catch (err) {
      toastGalatKeranjang(toast, err, err.response?.data?.detail ? pesanGalatPembeli(err.response.data.detail) : "Gagal menambahkan ke keranjang");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col bg-white border border-[#E5E5EA] rounded-[2px]" data-testid={`product-card-item-${p.id}`}>
      <Link to={href} className="block aspect-square bg-[#F5F5F7] overflow-hidden" data-testid={`product-card-link-${p.id}`}>
        {img ? (
          <img src={img} alt={p.nama} width="400" height="400" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : (
          <span className="w-full h-full flex items-center justify-center text-[#D2D2D7]"><Package size={36} strokeWidth={1.5} /></span>
        )}
      </Link>
      <div className="flex flex-col flex-1 pt-[10px] px-3 pb-3 md:px-4 md:pb-[14px]">
        <div className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#1D1D1F]">{p.kategori_nama || p.kategori}</div>
        <Link to={href} className="mt-1 block text-sm md:text-base font-medium text-[#1D1D1F] leading-snug min-h-[2.4em] md:min-h-0">
          {p.nama}
        </Link>
        <div className="mt-1 leading-none">
          <span className="text-xl md:text-2xl font-semibold text-[#1D1D1F]">{rupiah(p.harga_satuan)}</span>
          <span className="text-xs md:text-sm text-[#6E6E73] ml-1">/pcs</span>
        </div>
        <div className="mt-1 text-xs text-[#6E6E73]">sebelum diskon</div>
        <div className="mt-auto pt-3">
          <button
            onClick={tambah}
            disabled={busy || kosong}
            data-testid={`product-add-to-cart-${p.id}`}
            className={`w-full inline-flex items-center justify-center gap-2 rounded-[2px] py-[10px] text-[13px] md:text-sm font-medium text-white transition-colors disabled:opacity-50 ${
              diKeranjang ? "bg-[#1D1D1F]" : "bg-[#FF6B1A] hover:opacity-90"
            }`}
          >
            <ShoppingCart size={15} strokeWidth={1.8} />
            <span className="hidden md:inline">{kosong ? "Stok habis" : diKeranjang ? "Di keranjang" : "Tambah ke Keranjang"}</span>
            <span className="md:hidden">{kosong ? "Stok habis" : diKeranjang ? "Di keranjang" : "Tambah"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function BestSellers({ products }) {
  const list = (products || []).slice(0, 4);
  if (!list.length) return null;
  return (
    <section className="px-4 pt-6 pb-2 md:px-20 md:py-6" data-testid="best-sellers">
      <div className="mx-auto w-full max-w-[1280px]">
        <h2 className="text-2xl md:text-[30px] font-medium tracking-[-0.03em] text-[#1D1D1F]" data-testid="best-seller-title">
          Produk Terlaris
        </h2>
        <div className="mt-4 md:mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          {list.map((p) => <Card key={p.id} p={p} />)}
        </div>
      </div>
    </section>
  );
}
